import { errorEnvelope, type ErrorEnvelope } from '@signbento/contracts'

/**
 * Typed vault failures — mapped to registry codes (§13.2) so UI/services can
 * present the canonical error envelope. `MISSING_LOCAL_BYTES` must never be
 * silently repaired by re-fetching or replacing content (LOC-06).
 */
export type StorageErrorCode =
  | 'OP_STORAGE_MISSING_BYTES'
  | 'OP_STORAGE_QUOTA_EXCEEDED'
  | 'OP_STORAGE_CORRUPT_REVISION'
  | 'OP_STORAGE_REVISION_CONFLICT'
  | 'OP_STORAGE_WRITE_FAILED'
  | 'OP_STORAGE_MIGRATION_FAILED'

export class StorageError extends Error {
  readonly code: StorageErrorCode
  readonly current_revision?: number

  constructor(code: StorageErrorCode, message: string, options?: { current_revision?: number }) {
    super(message)
    this.name = 'StorageError'
    this.code = code
    if (options?.current_revision !== undefined) {
      this.current_revision = options.current_revision
    }
  }
}

export function isStorageError(value: unknown): value is StorageError {
  return value instanceof StorageError
}

export function storageErrorEnvelope(error: StorageError): ErrorEnvelope {
  return errorEnvelope(
    error.code,
    error.current_revision === undefined ? {} : { current_revision: error.current_revision },
  )
}

/** Normalize platform exceptions (IDB/OPFS) into typed storage failures. */
export function mapStorageFailure(error: unknown, fallbackMessage: string): StorageError {
  if (error instanceof StorageError) return error
  const name = (error as { name?: unknown }).name
  if (name === 'QuotaExceededError') {
    return new StorageError('OP_STORAGE_QUOTA_EXCEEDED', fallbackMessage)
  }
  return new StorageError('OP_STORAGE_WRITE_FAILED', fallbackMessage)
}
